import { EventEmitter } from "../../Utils/EventsEmmiter";

import type { ISceneDB } from "../../interfaces/Scenes";

import type { IStudio } from "../../screens/Dashboard/SubComponents/Studio/interfaces/Studio.interface";

import type { TemplateType } from "../../screens/Dashboard/SubComponents/Studio/interfaces/sub-interfaces/Template.interface";

import StrategyDraw from "./Strategy/StrategyDraw";

import type { StingerConfig } from "./Strategy/SceneTransition";

export type EventCallback = (data?: unknown) => void | Promise<void>;

interface IProps {
  model: IStudio;

  type: TemplateType;
}

interface LivePreviewEvents {
  changeModel: IStudio;

  screenshot: Blob;

  ended: undefined;

  [key: string]: unknown;
}

enum States {
  INACTIVE = "inactive",

  PLAYING = "playing",

  STOPPED = "stopped",
}

class LivePreview extends EventEmitter<LivePreviewEvents> {
  Model!: IStudio;

  Type!: TemplateType;

  Logo!: Blob;

  AudioContext!: AudioContext;

  MediaStreamDestination!: MediaStreamAudioDestinationNode;

  mediaElementSource!: MediaElementAudioSourceNode;

  private StrategyDrawing!: StrategyDraw;

  private OnLive: boolean = false;

  private Status: States = States.INACTIVE;

  constructor(props: IProps) {
    super();

    this.Model = props.model;

    this.Type = props.type;
  }

  getType() {
    return this.Type;
  }

  async updateModel(model: IStudio) {
    this.Model = model;

    await this.StrategyDrawing.updateModel(model);
  }

  async updateScene(scene: ISceneDB) {
    this.Model = scene.model;

    await this.StrategyDrawing.updateScene(scene);
  }

  updateOnLive(onLive: boolean) {
    this.OnLive = onLive;

    this.StrategyDrawing.updateOnLive(onLive);
  }

  destroyPlayers() {
    this.StrategyDrawing.destroyPlayers();
  }

  deleteArea(id: string) {
    this.StrategyDrawing.deleteArea(id);
  }

  setAudioContext(
    audioContext: AudioContext,
    destination: MediaStreamAudioDestinationNode,
  ) {
    try {
      this.AudioContext = audioContext;

      this.MediaStreamDestination = destination;

      return this;
    } catch (error) {
      console.error(error);

      return this;
    }
  }

  async addImage(logo: Blob) {
    this.Logo = logo;
  }

  async play() {
    this.StrategyDrawing = new StrategyDraw(this);

    await this.StrategyDrawing.initialize();

    this.Status = States.PLAYING;
  }

  async switchScene(scene: ISceneDB, isStreaming: boolean) {
    this.Model = scene.model;

    this.OnLive = isStreaming;

    await this.StrategyDrawing.switchScene(scene, this.OnLive);
  }

  async prewarmScene(scene: ISceneDB) {
    if (!this.StrategyDrawing) return;

    await this.StrategyDrawing.prewarmScene(scene);
  }

  /** Miniatura de una escena fuera de pantalla; `null` si el render aún no está listo. */
  async snapshotScene(scene: ISceneDB, width: number, height: number) {
    if (!this.StrategyDrawing) return null;

    return this.StrategyDrawing.snapshotScene(scene, width, height);
  }

  async prewarmStinger(cfg: StingerConfig) {
    if (!this.StrategyDrawing) return;

    await this.StrategyDrawing.prewarmStinger(cfg);
  }

  stopScene() {
    this.StrategyDrawing.stopScene();
  }

  resumeScene() {
    this.StrategyDrawing.resumeScene();
  }

  async stop() {
    this.Status = States.STOPPED;

    this.StrategyDrawing.destroy();
  }

  async getStream(): Promise<MediaStream> {
    try {
      const stream = this.StrategyDrawing.getStream();

      return stream;
    } catch (error) {
      console.error("Error getting stream:", error);

      return new MediaStream();
    }
  }

  getModel(): IStudio {
    return this.Model;
  }

  getStatus(): States {
    return this.Status;
  }

  isPlaying(): boolean {
    return this.Status === States.PLAYING;
  }

  isStopped(): boolean {
    return this.Status === States.STOPPED;
  }

  isInactive(): boolean {
    return this.Status === States.INACTIVE;
  }

  public emitEvent<K extends keyof LivePreviewEvents>(
    event: K,
    data?: LivePreviewEvents[K],
  ): void {
    this.emit(event, data);
  }
}

export default LivePreview;
